import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/api';
import { useAuthStore } from '../store';
import { GlassButton, GlassInput } from '../components/GlassUI';
import { FaGem, FaEnvelope, FaLock, FaUserPlus, FaSignInAlt, FaCrown, FaShieldAlt } from 'react-icons/fa';

const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { setUser, setToken, setSubscription, isAuthenticated } = useAuthStore();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  // Already signed in? go straight to the dashboard.
  useEffect(() => {
    if (isAuthenticated) navigate('/dashboard');
  }, [isAuthenticated, navigate]);

  // ─── Guest demo login (button) ─────────────────────────────────
  // Lets prospective customers explore the app before buying, without
  // creating an account. Enabled when REACT_APP_DEMO_MODE === 'true'.
  const handleDemoLogin = async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const response = await authService.demoLogin();
      const { user, token, subscription } = response.data;
      if (user && token) {
        setUser(user);
        setToken(token);
        if (subscription) setSubscription(subscription);
        navigate('/dashboard');
      } else {
        setErrorMsg('Could not start the demo. Please try again.');
      }
    } catch (err: any) {
      console.error('Demo login failed:', err);
      setErrorMsg('Demo is unavailable right now. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    phoneNumber: '',
    staffName: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrorMsg('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg('');

    try {
      const endpoint = isLogin ? authService.login : authService.register;
      const payload = isLogin
        ? { email: formData.email, password: formData.password }
        : formData;

      const response = await endpoint(payload);
      const { user, token, subscription } = response.data;

      if (user && token) {
        setUser(user);
        setToken(token);
        if (subscription) {
          setSubscription(subscription);
        }
        navigate('/dashboard');
      } else {
        setErrorMsg('Invalid response from server');
      }
    } catch (error: any) {
      console.error('Auth error:', error);
      const msg =
        error?.response?.data?.error ||
        error?.message ||
        'Authentication failed. Check your credentials.';
      setErrorMsg(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800">
      {/* Decorative gold glow */}
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-amber-500/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-amber-400/10 blur-3xl pointer-events-none" />
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />

      <div className="w-full max-w-md relative animate-fade-in-up">
        {/* Brand */}
        <div className="text-center mb-8">
          <div className="inline-flex w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 items-center justify-center shadow-lg shadow-amber-500/30 mb-4">
            <FaGem className="text-white text-3xl" />
          </div>
          <h1 className="text-3xl font-bold text-white tracking-tight">ApniCRM</h1>
          <p className="text-slate-400 mt-1.5">Jewelry Store Management System</p>
        </div>

        <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl shadow-black/30 border border-white/10 p-6 sm:p-8">
          {/* Tabs */}
          <div className="grid grid-cols-2 gap-1 bg-slate-100 rounded-xl p-1 mb-6">
            <button
              onClick={() => { setIsLogin(true); setErrorMsg(''); }}
              className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                isLogin ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <FaSignInAlt className="text-xs" /> Login
            </button>
            <button
              onClick={() => { setIsLogin(false); setErrorMsg(''); }}
              className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                !isLogin ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <FaUserPlus className="text-xs" /> Register
            </button>
          </div>

          {errorMsg && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl animate-fade-in">
              <p className="text-red-700 text-sm font-medium">{errorMsg}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <div>
                  <label className="block text-slate-700 font-medium mb-1.5 text-sm">Full Name *</label>
                  <GlassInput
                    type='text'
                    name='name'
                    placeholder='Enter your full name'
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <label className="block text-slate-700 font-medium mb-1.5 text-sm">Staff Name</label>
                  <GlassInput
                    type='text'
                    name='staffName'
                    placeholder='What should we call you?'
                    value={formData.staffName}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label className="block text-slate-700 font-medium mb-1.5 text-sm">Phone Number *</label>
                  <GlassInput
                    type='tel'
                    name='phoneNumber'
                    placeholder='10-digit mobile number'
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    required
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-slate-700 font-medium mb-1.5 text-sm">Email</label>
              <div className="relative">
                <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
                <GlassInput
                  type='email'
                  name='email'
                  placeholder='you@example.com'
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="pl-11"
                />
              </div>
            </div>

            <div>
              <label className="block text-slate-700 font-medium mb-1.5 text-sm">Password</label>
              <div className="relative">
                <FaLock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
                <GlassInput
                  type='password'
                  name='password'
                  placeholder='••••••••'
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="pl-11"
                />
              </div>
            </div>

            <GlassButton
              type='submit'
              variant='gold'
              className='w-full mt-6 py-3 text-base'
              disabled={loading}
            >
              {loading ? 'Processing...' : isLogin ? 'Login to Dashboard' : 'Create Account'}
            </GlassButton>
          </form>

          {/* Guest demo — explore the app before buying */}
          {isDemoMode && (
            <div className="mt-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-px flex-1 bg-slate-200" />
                <span className="text-xs text-slate-400 font-medium">or</span>
                <div className="h-px flex-1 bg-slate-200" />
              </div>
              <button
                type='button'
                onClick={handleDemoLogin}
                disabled={loading}
                className="w-full py-3 rounded-xl text-base font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 hover:border-emerald-300 transition-all disabled:opacity-60 flex items-center justify-center gap-2"
              >
                <FaGem className="text-emerald-500 text-sm" />
                {loading ? 'Opening demo…' : 'View Live Demo — no login needed'}
              </button>
              <p className="text-center text-xs text-slate-400 mt-2">
                Explore the full app with sample data before you buy.
              </p>
            </div>
          )}

          {/* Super Admin quick fill */}
          <div className="mt-5 p-3.5 bg-gradient-to-r from-slate-50 to-amber-50 border border-amber-200/70 rounded-xl text-center">
            <p className="text-xs text-slate-500 mb-1.5 flex items-center justify-center gap-1">
              <FaCrown className="text-amber-500" /> Super Admin — tap to autofill
            </p>
            <button
              type='button'
              onClick={() => {
                setFormData((prev) => ({ ...prev, email: 'admin@apni.com', password: 'Admin@123' }));
                setErrorMsg('');
              }}
              className="text-sm text-slate-700 font-mono font-semibold transition-colors hover:text-amber-700 bg-white border border-slate-200 rounded-lg px-3 py-1.5 w-full hover:border-amber-300"
            >
              admin@apni.com / Admin@123
            </button>
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 text-slate-400 text-xs mt-6">
          <FaShieldAlt className="text-emerald-400" />
          <span>100% secure · Encrypted · Auto database backups · Zero data loss</span>
        </div>
        <p className="text-center text-slate-500 text-xs mt-2">
          © {new Date().getFullYear()} ApniCRM — Jewelry Store Management
        </p>
      </div>
    </div>
  );
};

export default LoginPage;